import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import titleize from 'titleize';
import { CopyToClipboard } from '../../../components/CopyToClipboard';
import { TargetTypeLabel } from '../../../components/Curation/Labels';
import useAlertDialog from '../../../hooks/useAlertDialog';
import useBackdrop from '../../../hooks/useBackdrop';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import {
  IUserSegment,
  IUserSegmentAutoUpdate,
  IUserSegmentGroup,
  IUserSegmentProduct,
  IUserSegmentSystem,
  IUserSegmentTrigger,
  IUserSegmentWebhook,
  UserSegmentType,
} from '../../../services/types';
import { RouteNames } from '../../config';
import useStyles from './styles';
import cx from 'classnames';

type IUserSegmentAll =
  | IUserSegmentAutoUpdate
  | IUserSegmentGroup
  | IUserSegmentTrigger
  | IUserSegmentWebhook
  | IUserSegmentSystem
  | IUserSegmentProduct;

interface IProps {
  userSegmentAll: Array<IUserSegmentAll> | undefined;
  page: number;
  totalCount: number;
  isActive: boolean;
  rowsPerPage: number;
  onClickRowItem: (segmentItem: IUserSegment) => void;
  onError: (error: Error) => void;
  onClickCuration: (segmentItem: IUserSegment, mode: 'create' | 'edit') => void;
  setIsActive: (active: boolean) => void;
  fetchAllTypeData: () => void;
  setPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;
  onDelete: (seg: IUserSegmentAll, type: UserSegmentType | 'all', idx: number) => void;
  onToggleStatus: (seg: IUserSegmentAll, type: UserSegmentType | 'all') => void;
}

export const AllList = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const confirm = useConfirmDialog();
  const alert = useAlertDialog();
  const backdrop = useBackdrop();
  const history = useHistory();

  const [userSegmentAll, setUserSegmentAll] = useState<Array<IUserSegmentAll>>();

  useEffect(() => {
    setUserSegmentAll(props.userSegmentAll);
  }, [props.userSegmentAll]);

  if (userSegmentAll === undefined) {
    return (
      <Paper>
        <Box padding={2} textAlign="center">
          <CircularProgress size={30} />
        </Box>
      </Paper>
    );
  }

  return (
    <div className={classes.root}>
      <Box paddingBottom={1}>
        <RadioGroup
          row
          value={props.isActive ? 'true' : 'false'}
          name="isActive"
          onChange={e => {
            props.setIsActive(e.target.value === 'true');
          }}
        >
          <FormControlLabel control={<Radio value="true" color="primary" className={''} />} label="Activated" />
          <FormControlLabel control={<Radio value="false" color="primary" />} label="Deactivated" />
        </RadioGroup>
      </Box>
      {userSegmentAll.length === 0 && (
        <Paper>
          <Box display="flex" justifyContent="center" alignItems="center" className={classes.noDataBox} flexDirection="column">
            <Typography>No data</Typography>
            <Typography variant="caption">{'Tap the "Create User Segment" at the top to create a first user segment.'}</Typography>
          </Box>
        </Paper>
      )}
      {userSegmentAll.length > 0 && (
        <Paper>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell align="center" style={{ width: 60 }}>
                  Segment
                  <br />
                  ID
                </TableCell>
                <TableCell align="center" style={{ width: 96 }}>
                  Type
                </TableCell>
                <TableCell>Segment Title</TableCell>
                <TableCell align="center">User Count</TableCell>
                <TableCell align="center">
                  Max att.
                  <br />
                  duration
                </TableCell>
                <TableCell align="center">Persistence</TableCell>
                <TableCell align="center">Reentering</TableCell>
                <TableCell align="center">Curation</TableCell>
                <TableCell align="center"></TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userSegmentAll.length > 0 &&
                userSegmentAll.map((seg, idx) => {
                  return (
                    <TableRow
                      component={Link}
                      to={`${RouteNames.UserSegment}/v/${seg.id}`}
                      key={seg.id}
                      hover
                      className={cx({ [classes.tableRow]: true, [classes.tableRowHighlited]: seg.type === UserSegmentType.System || seg.id === 45 })}
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        if (!seg.id) {
                          return;
                        }

                        props.onClickRowItem(seg);
                      }}
                    >
                      <TableCell align="center">
                        <CopyToClipboard content={seg.id!} />
                      </TableCell>
                      <TableCell align="center" style={{ textTransform: 'capitalize' }}>
                        {seg.type}
                      </TableCell>
                      <TableCell>
                        <span className={classes.nowrapDots}>
                          {seg.isGreedy && <Chip size="small" label="Greedy" color="secondary" style={{ marginRight: 8 }} />}
                          {seg.name}
                        </span>
                      </TableCell>
                      <TableCell align="center">{seg.type === UserSegmentType.System ? '-' : seg.userCount?.toLocaleString() || 0}</TableCell>
                      <TableCell align="center">{seg.type === UserSegmentType.System ? '-' : seg.daysToExpiration}</TableCell>
                      <TableCell align="center">{seg.type === UserSegmentType.System ? '-' : titleize(seg.persistentType || '')}</TableCell>
                      <TableCell align="center">{seg.type === UserSegmentType.System ? '-' : seg.brazeTriggerWhenReentering ? 'Y' : 'N'}</TableCell>
                      <TableCell align="center">
                        {(seg.featuredCardNum || 0) + (seg.collectionNum || 0) === 0 ? (
                          <Button
                            // href={`${RouteNames.UserSegment}/v/${seg.id}`}
                            variant="outlined"
                            color="primary"
                            className={classes.segButton}
                            onClick={e => {
                              props.onClickCuration(seg, 'create');
                              history.push(`${RouteNames.UserSegment}/v/${seg.id}/curation/create`);
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            Create
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            color="default"
                            className={classes.segButton}
                            onClick={e => {
                              props.onClickCuration(seg, 'edit');
                              history.push(`${RouteNames.UserSegment}/v/${seg.id}/curation`);
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            Edit
                          </Button>
                        )}
                      </TableCell>
                      <TableCell align="left">
                        <Box className={classes.abtestCountBox}>
                          <Box className={classes.abtestCountBoxLeft}>Featured Card: {seg.featuredCardNum}</Box>
                          <Box className={classes.abtestCountBoxRight}>{seg.isFeaturedCardsOnTest && <TargetTypeLabel type={'A/B'} />}</Box>
                        </Box>
                        <Box className={classes.abtestCountBox}>
                          <Box className={classes.abtestCountBoxLeft}>Collection: {seg.collectionNum}</Box>
                          <Box className={classes.abtestCountBoxRight}>{seg.isCollectionsOnTest && <TargetTypeLabel type={'A/B'} />}</Box>
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        {seg.type === UserSegmentType.System || seg.type === UserSegmentType.Group || seg.id === 45 ? (
                          '-'
                        ) : (
                          <Tooltip title={seg.isActive ? 'Activated' : 'Deactivated'} placement="bottom">
                            <Switch
                              color={'primary'}
                              checked={seg.isActive}
                              onClick={e => e.stopPropagation()}
                              onChange={e => {
                                props.onToggleStatus(seg, 'all');
                              }}
                              value={idx}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {seg.type === UserSegmentType.System || seg.id === 45 ? (
                          '-'
                        ) : (
                          <Tooltip
                            title={seg.type !== UserSegmentType.Group && seg.isActive ? 'You cannot delete a segment in its active state.' : ''}
                            placement="left-end"
                          >
                            <IconButton
                              style={{
                                opacity: seg.type !== UserSegmentType.Group && seg.isActive ? 0.25 : 1,
                              }}
                              color={seg.type !== UserSegmentType.Group && seg.isActive ? 'default' : 'primary'}
                              disableFocusRipple={seg.isActive}
                              disableTouchRipple={seg.isActive}
                              disableRipple={seg.isActive}
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                props.onDelete(seg, 'all', idx);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          {props.totalCount !== undefined && (
            <TablePagination
              count={props.totalCount}
              rowsPerPage={props.rowsPerPage}
              page={props.page}
              onChangePage={(e: unknown, v: number) => {
                props.setPage(v);
              }}
              onChangeRowsPerPage={(event: React.ChangeEvent<HTMLInputElement>) => {
                const rpp = parseInt(event.target.value, 10);
                props.setRowsPerPage(rpp);
                props.setPage(0);
              }}
            />
          )}
        </Paper>
      )}
    </div>
  );
};
